import type {
  AgentActiveContext,
  AgentProposedCellAdd,
  AgentProposedCellDelete,
  AgentProposedCellDiff,
} from "./AgentSidebarContext";
import type { AgentSidebarStatus } from "./agent-conversation-types";

export interface AssistantChat {
  id: string;
  title: string;
  minimized: boolean;
  conversationId: string | null;
  requestedConversationId: string | null;
  activeContext: AgentActiveContext | null;
  pendingPrompt: string | null;
  proposedCellDiff: AgentProposedCellDiff | null;
  proposedCellAdd: AgentProposedCellAdd | null;
  proposedCellDelete: AgentProposedCellDelete | null;
  status: AgentSidebarStatus["kind"];
  // Window size the user dragged this chat's panel to; null takes the panel's default. Bounds live
  // with the panel, which is the only writer.
  width: number | null;
  height: number | null;
}

// Chats on the rail, one tab each. At most one is open as a window at a time: opening or focusing
// any chat minimizes the rest, so switching tabs swaps which conversation the window shows while
// every other chat keeps its session running behind its tab.
export interface AssistantWorkspaceState {
  chats: AssistantChat[];
  activeId: string | null;
}

// Open chats, minimized or not; saved history is unbounded. The rail disables its new-chat button
// at the cap, and the reducer refuses to open past it for every other entry point.
export const ASSISTANT_MAX_OPEN_CHATS = 20;
export const isAtAssistantChatLimit = (state: AssistantWorkspaceState) =>
  state.chats.length >= ASSISTANT_MAX_OPEN_CHATS;

// A window nothing has happened in yet: no conversation started or requested and no turn running.
// Asking for a new chat while one exists brings it forward instead of opening another.
export const isBlankAssistantChat = (chat: AssistantChat) =>
  chat.conversationId == null && chat.requestedConversationId == null && chat.status === "idle";
export const findBlankAssistantChat = (state: AssistantWorkspaceState) =>
  state.chats.find(isBlankAssistantChat);

export const createAssistantChat = (id: string): AssistantChat => ({
  id,
  title: "New chat",
  minimized: false,
  conversationId: null,
  requestedConversationId: null,
  activeContext: null,
  pendingPrompt: null,
  proposedCellDiff: null,
  proposedCellAdd: null,
  proposedCellDelete: null,
  status: "idle",
  width: null,
  height: null,
});

export type AssistantWorkspaceAction =
  // Opens a blank chat as a new tab, or brings forward the blank one already on the rail.
  | { type: "new"; id: string }
  // Opens a saved conversation as a new tab, or focuses the chat already showing it.
  | { type: "request"; id: string; conversationId: string }
  | { type: "close"; id: string }
  | { type: "focus"; id: string }
  | { type: "toggle"; id?: string; fallbackId: string }
  | { type: "update"; id?: string; fallbackId: string; patch: Partial<Omit<AssistantChat, "id">> };

// Minimizes every chat but `openId`, keeping the one-window invariant whenever a chat opens.
const minimizeOthers = (chats: AssistantChat[], openId: string) =>
  chats.map((chat) => (chat.id === openId || chat.minimized ? chat : { ...chat, minimized: true }));

// Adds `chat` as a new tab and makes it the open window. It takes the dragged size of the window
// it displaces so the window does not jump size when a new chat opens.
const openTab = (state: AssistantWorkspaceState, chat: AssistantChat) => {
  if (isAtAssistantChatLimit(state)) return state;
  const displaced = state.chats.find((existing) => existing.id === state.activeId);
  const opened = { ...chat, width: displaced?.width ?? null, height: displaced?.height ?? null };
  return { chats: minimizeOthers([...state.chats, opened], opened.id), activeId: opened.id };
};

export const assistantWorkspaceReducer = (
  state: AssistantWorkspaceState,
  action: AssistantWorkspaceAction,
): AssistantWorkspaceState => {
  if (action.type === "new") {
    const blank = findBlankAssistantChat(state);
    if (blank != null) return assistantWorkspaceReducer(state, { type: "focus", id: blank.id });
    return openTab(state, createAssistantChat(action.id));
  }
  if (action.type === "request") {
    const existing = state.chats.find((chat) => chat.conversationId === action.conversationId);
    if (existing) return assistantWorkspaceReducer(state, { type: "focus", id: existing.id });
    return openTab(state, {
      ...createAssistantChat(action.id),
      conversationId: action.conversationId,
      requestedConversationId: action.conversationId,
    });
  }
  if (action.type === "close") {
    const index = state.chats.findIndex((chat) => chat.id === action.id);
    const chats = state.chats.filter((chat) => chat.id !== action.id);
    return {
      chats,
      activeId:
        state.activeId === action.id
          ? (chats[index] ?? chats[index - 1])?.id ?? null
          : state.activeId,
    };
  }
  if (action.type === "focus") {
    const chat = state.chats.find((chat) => chat.id === action.id);
    if (chat == null || (state.activeId === action.id && !chat.minimized)) return state;
    return {
      chats: minimizeOthers(
        state.chats.map((chat) => (chat.id === action.id ? { ...chat, minimized: false } : chat)),
        action.id,
      ),
      activeId: action.id,
    };
  }
  const id = action.id ?? state.activeId ?? action.fallbackId;
  const existing = state.chats.find((chat) => chat.id === id);
  // A late stream update must not resurrect a tab explicitly closed by the user.
  if (action.id != null && existing == null) return state;
  const patch =
    action.type === "toggle" ? { minimized: existing ? !existing.minimized : false } : action.patch;
  if (
    existing != null &&
    Object.entries(patch).every(([key, value]) => existing[key as keyof AssistantChat] === value)
  ) {
    return state;
  }
  // Cleanup from a page with no chat must not create an empty tab.
  if (
    existing == null &&
    !Object.entries(patch).some(([key, value]) =>
      key === "minimized" ? value === false : value != null,
    )
  )
    return state;
  // A patch aimed at the focused chat only creates a chat when none is on the rail.
  if (existing == null && state.chats.length > 0) return state;
  const updated = { ...(existing ?? createAssistantChat(id)), ...patch };
  const chats = existing
    ? state.chats.map((chat) => (chat.id === id ? updated : chat))
    : [...state.chats, updated];
  // Opening a chat by patch (a notebook entry point, or toggling it) takes over the window too.
  if (existing?.minimized !== false && !updated.minimized) {
    return { chats: minimizeOthers(chats, id), activeId: id };
  }
  return { chats, activeId: state.activeId ?? id };
};
